import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchZipcodeInfo } from '@/services/zipcode';
import type { ZipcodeInfo } from '@/types/zipcode';
import styles from './ZipcodeInput.module.css';

interface ZipcodeInputProps {
  value: string;
  onChange: (zipcode: string, info: ZipcodeInfo | null) => void;
  error?: string;
}

export function ZipcodeInput({ value, onChange, error }: ZipcodeInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const [locationInfo, setLocationInfo] = useState<ZipcodeInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const initializedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const lookupZipcode = useCallback(async (zipcode: string) => {
    if (zipcode.length !== 5) {
      setLocationInfo(null);
      return null;
    }

    setIsLoading(true);
    try {
      const info = await fetchZipcodeInfo(zipcode);
      setLocationInfo(info);
      return info;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initializedRef.current && value && value.length === 5) {
      initializedRef.current = true;
      void lookupZipcode(value).then((info) => {
        onChangeRef.current(value, info);
      });
    }
  }, [value, lookupZipcode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 5);
    setInputValue(newValue);

    if (newValue.length === 5) {
      void lookupZipcode(newValue).then((info) => {
        onChange(newValue, info);
      });
    } else {
      setLocationInfo(null);
      onChange(newValue, null);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputWrapper}>
        <input
          type="text"
          inputMode="numeric"
          placeholder="Enter ZIP code"
          value={inputValue}
          onChange={handleInputChange}
          className={`${styles.input} ${error ? styles.inputError : ''}`}
          aria-label="ZIP code"
          aria-describedby={error ? 'zipcode-error' : undefined}
        />
        {isLoading && <span className={styles.loader} />}
      </div>
      {locationInfo && (
        <p className={styles.location}>
          {locationInfo.placeName}, {locationInfo.stateAbbreviation}
        </p>
      )}
      {error && (
        <p id="zipcode-error" className={styles.error}>
          {error}
        </p>
      )}
    </div>
  );
}
